import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import 'assets/css/dateRange.css';

interface DateRangeProps {
  onDateChange: (dateRange: [Date | null, Date | null]) => void;
  disabled?: boolean;
  id?: string;
  cStartdate?: any;
  cEnddate?: any;
}

/* Inject global styles once into <head> so they override the portal-rendered popper */
const GLOBAL_ID = '__dr_global_styles__';
function injectGlobalStyles() {
  if (typeof document === 'undefined' || document.getElementById(GLOBAL_ID)) return;
  const s = document.createElement('style');
  s.id = GLOBAL_ID;
  s.textContent = `
    /* ── DateRange global overrides ── */
    .react-datepicker-popper { z-index: 99999 !important; }
    .react-datepicker {
      font-family: 'Plus Jakarta Sans', sans-serif !important;
      border: 1.5px solid #e2e8f0 !important;
      border-radius: 14px !important;
      box-shadow: 0 8px 32px rgba(15,23,42,0.14) !important;
      overflow: hidden;
    }
    .react-datepicker__header {
      background: #f8fafc !important;
      border-bottom: 1px solid #e2e8f0 !important;
      border-radius: 0 !important;
      padding-top: 10px !important;
    }
    .react-datepicker__current-month {
      font-size: 13px !important; font-weight: 700 !important; color: #0f172a !important;
    }
    .react-datepicker__day-name {
      font-size: 11px !important; font-weight: 700 !important; color: #94a3b8 !important;
    }
    .react-datepicker__day {
      font-size: 12.5px !important; font-weight: 500 !important; color: #374151 !important; border-radius: 7px !important;
    }
    .react-datepicker__day:hover { background: #eff6ff !important; color: #1d4ed8 !important; }
    .react-datepicker__day--selected,
    .react-datepicker__day--in-range,
    .react-datepicker__day--range-start,
    .react-datepicker__day--range-end { background: #2563eb !important; color: #fff !important; }
    .react-datepicker__day--in-selecting-range { background: #bfdbfe !important; color: #1d4ed8 !important; }
    .react-datepicker__day--keyboard-selected { background: #eff6ff !important; color: #1d4ed8 !important; }
    .react-datepicker__navigation-icon::before { border-color: #64748b !important; }
    .react-datepicker__close-icon::after {
      background-color: #94a3b8 !important; font-size: 13px !important;
      width: 16px !important; height: 16px !important;
    }
    .react-datepicker__close-icon:hover::after { background-color: #dc2626 !important; }

    /* ── Mobile calendar: constrain to viewport ── */
    @media (max-width: 600px) {
      .react-datepicker-popper {
        /* Prevent it going off right edge */
        max-width: calc(100vw - 24px) !important;
        left: 12px !important;
        right: auto !important;
        /* Override any inline transform-based positioning */
        transform: translate3d(0px, 42px, 0px) !important;
      }
      .react-datepicker {
        width: calc(100vw - 24px) !important;
        border-radius: 12px !important;
      }
      .react-datepicker__month-container {
        width: 100% !important;
        float: none !important;
      }
      /* Equally distribute day cells */
      .react-datepicker__week,
      .react-datepicker__day-names {
        display: flex !important;
        justify-content: space-between !important;
        padding: 0 6px !important;
      }
      .react-datepicker__day-name,
      .react-datepicker__day {
        flex: 1 !important;
        width: auto !important;
        margin: 1px 0 !important;
        font-size: 12px !important;
      }
      .react-datepicker__current-month { font-size: 12.5px !important; }
    }

    @media (max-width: 380px) {
      .react-datepicker-popper {
        max-width: calc(100vw - 16px) !important;
        left: 8px !important;
      }
      .react-datepicker { width: calc(100vw - 16px) !important; }
      .react-datepicker__day { font-size: 11px !important; }
      .react-datepicker__day-name { font-size: 10px !important; }
    }
  `;
  document.head.appendChild(s);
}

const DateRange: React.FC<DateRangeProps> = ({ onDateChange, disabled, id, cStartdate, cEnddate }) => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const [startDate, endDate] = dateRange;
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    injectGlobalStyles();
    const check = () => setIsMobile(window.innerWidth <= 600);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => { onDateChange(dateRange); }, [dateRange]);

  return (
    <div className="dr-wrap">
      <div className="dr-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </div>

      <DatePicker
        id={id}
        className="dr-input"
        selectsRange={true}
        startDate={startDate || cStartdate}
        endDate={endDate || cEnddate}
        placeholderText="Select date range"
        onChange={(update: [Date, Date] | null) => { if (update) setDateRange(update); }}
        isClearable={true}
        disabled={disabled}
        onKeyDown={(e) => e.preventDefault()}
        withPortal={isMobile}
        portalId={isMobile ? "dr-portal" : undefined}
        popperPlacement="bottom-start"
      />

      <style>{`
        .dr-wrap {
          position: relative; display: flex; align-items: center; width: 100%;
        }
        .dr-icon {
          position: absolute; left: 12px; color: #94a3b8;
          display: flex; align-items: center; pointer-events: none; z-index: 1;
        }
        .dr-input {
          width: 100% !important;
          height: 42px;
          padding: 0 36px 0 36px;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 500; color: #0f172a;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
          cursor: pointer; box-sizing: border-box; -webkit-appearance: none;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .dr-input::placeholder { color: #cbd5e1; }
        .dr-input:hover:not(:disabled) { border-color: #cbd5e1; background: #f1f5f9; }
        .dr-input:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .dr-input:disabled { opacity: 0.55; cursor: not-allowed; background: #f1f5f9; }

        /* Portal overlay on mobile */
        .react-datepicker__portal {
          background: rgba(15,23,42,0.45) !important;
          backdrop-filter: blur(3px) !important;
          z-index: 99999 !important;
        }
        .react-datepicker__portal .react-datepicker {
          border-radius: 18px !important;
          box-shadow: 0 24px 60px rgba(15,23,42,0.2) !important;
          overflow: hidden;
        }
        /* Make portal calendar scrollable on very short screens */
        .react-datepicker__portal .react-datepicker__month-container {
          max-height: 80vh; overflow-y: auto;
        }

        @media (max-width: 480px) {
          .dr-input { font-size: 12px; height: 40px; padding: 0 32px 0 34px; }
          .dr-icon svg { width: 13px; height: 13px; }
        }
        @media (max-width: 380px) {
          .dr-input { font-size: 11.5px; height: 38px; }
        }
      `}</style>
    </div>
  );
};

export default DateRange;